import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectSite } from '../../../../features/site/siteSlice';
import { useLiveData } from '../../../site/live-data/liveDataApi';
import { CHART_DEFAULT_OPTIONS, CHART_SPACER, SOURCES_COLOURS } from '../constants';

export function useSourcesChartData() {
  const { t } = useTranslation();
  const { is_consumption_site: isConsumptionSite } = useSelector(selectSite);
  const { data, isMeterError, isLoading, isFetching } = useLiveData();

  const seriesData = useMemo(() => {
    const { solar, grid, battery } = data;
    const solarProduction = Math.max(solar, 0);
    const gridImport = Math.max(grid, 0);
    const batteryDischarge = Math.abs(Math.min(battery ?? 0, 0));
    const totalSources = solarProduction + gridImport;

    if (isLoading || totalSources === 0) return [];

    return [
      ...(!isConsumptionSite && solarProduction
        ? [
            CHART_SPACER,
            {
              name: t('Solar.solar'),
              y: Math.min((Math.abs(solarProduction) / totalSources) * 100, 100),
              color: SOURCES_COLOURS.solar,
            },
          ]
        : []),
      ...(gridImport > 0
        ? [
            CHART_SPACER,
            {
              name: t('Grid.grid'),
              y: Math.min((gridImport / totalSources) * 100, 100),
              color: SOURCES_COLOURS.grid,
            },
          ]
        : []),
      ...(batteryDischarge > 0
        ? [
            CHART_SPACER,
            {
              name: t('Battery.battery'),
              y: Math.min((batteryDischarge / totalSources) * 100, 100),
              color: SOURCES_COLOURS.battery,
            },
          ]
        : []),
    ];
  }, [data, isLoading, isConsumptionSite, t]);

  const options = useMemo(
    () => ({
      ...CHART_DEFAULT_OPTIONS,
      plotOptions: {
        pie: {
          ...CHART_DEFAULT_OPTIONS.plotOptions.pie,
          innerSize: 280,
        },
      },
      series: [
        {
          data: seriesData,
        },
      ],
    }),
    [seriesData]
  );

  return {
    isLoading,
    isFetching,
    isError: isMeterError,
    data,
    options,
  };
}
